// S01 Sales productivity + M13 sample size.
import { useMemo } from "react";
import { salesProductivity, sampleSize, inr } from "./engines";
import { Field, SliderField, ResultRow, Note, useNumbers } from "./ui";

export function ToolS01() {
  const [v, set] = useNumbers({ reps: 10, sellingDaysPerMonth: 22, hoursPerDay: 8, travelAdminPct: 45, visitsPerDay: 6, conversionPct: 20, avgOrderValue: 25000, monthlyRevenue: 5000000 });
  const r = useMemo(() => salesProductivity(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Sales reps" value={v.reps} onChange={set("reps")} testid="s01-reps" />
          <Field label="Selling days / month" value={v.sellingDaysPerMonth} onChange={set("sellingDaysPerMonth")} testid="s01-days" />
          <Field label="Hours / day" value={v.hoursPerDay} onChange={set("hoursPerDay")} testid="s01-hours" />
          <Field label="Productive visits / day" value={v.visitsPerDay} onChange={set("visitsPerDay")} testid="s01-visits" />
          <Field label="Visit → order conversion" suffix="%" value={v.conversionPct} onChange={set("conversionPct")} testid="s01-conv" />
          <Field label="Average order value" prefix="₹" value={v.avgOrderValue} onChange={set("avgOrderValue")} testid="s01-aov" />
        </div>
        <SliderField label="Time lost to travel & admin" value={v.travelAdminPct} onChange={set("travelAdminPct")} min={10} max={80} testid="s01-travel" />
        <Field label="Actual team revenue / month (optional)" prefix="₹" value={v.monthlyRevenue} onChange={set("monthlyRevenue")} testid="s01-actual" />
        <Note>Activity is not productivity. Compare the modelled potential against actuals to see whether the constraint is time, conversion or territory.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Productivity picture</div>
        <div className="mt-3">
          <ResultRow label="Selling hours / rep / month" value={r.sellingHoursPerRepMonth} />
          <ResultRow label="Visits → orders / rep / month" value={`${r.visitsPerRepMonth} → ${r.ordersPerRepMonth}`} />
          <ResultRow strong label="Modelled revenue / rep / month" value={inr(r.modelledRevenuePerRep)} />
          {r.actualRevenuePerRep !== null && (
            <ResultRow label="Actual revenue / rep" value={inr(r.actualRevenuePerRep)} tone={r.actualRevenuePerRep < r.modelledRevenuePerRep ? "bad" : "good"} />
          )}
        </div>
        <div className="mt-5 overline">If productivity improves…</div>
        <table className="w-full text-sm mt-2">
          <tbody>
            {r.scenarios.map((s) => (
              <tr key={s.upliftPct} className="border-b border-black/5">
                <td className="py-2 text-zinc-600">+{s.upliftPct}% productivity</td>
                <td className="py-2 tabular-nums text-right">{inr(s.revenuePerRep)} / rep</td>
                <td className="py-2 tabular-nums text-right font-medium">{inr(s.teamRevenue)} team</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ToolM13() {
  const [v, set] = useNumbers({ confidencePct: 95, marginPct: 5, proportionPct: 50, population: 0 });
  const r = useMemo(() => sampleSize(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Confidence level" suffix="%" hint="80 / 90 / 95 / 99" value={v.confidencePct} onChange={set("confidencePct")} testid="m13-conf" />
          <Field label="Margin of error" suffix="±%" value={v.marginPct} onChange={set("marginPct")} testid="m13-margin" />
          <Field label="Expected proportion" suffix="%" hint="50% = most conservative" value={v.proportionPct} onChange={set("proportionPct")} testid="m13-prop" />
          <Field label="Population size" hint="0 = unknown / infinite" value={v.population} onChange={set("population")} testid="m13-pop" />
        </div>
        <Note>Assumes simple random sampling. Opt-in or convenience panels do not carry this statistical margin of error — treat their results as directional.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Required sample</div>
        <div className="mt-4 font-serif-display text-5xl" data-testid="m13-result">{r.n.toLocaleString("en-IN")}</div>
        <div className="text-sm text-zinc-500 mt-1">completed responses{r.finiteCorrection ? " (finite-population corrected)" : ""}</div>
        <div className="mt-4">
          <ResultRow label="Before population correction" value={r.nRaw.toLocaleString("en-IN")} />
          <ResultRow label="Suggested invitations (30% response)" value={Math.ceil(r.n / 0.3).toLocaleString("en-IN")} />
        </div>
      </div>
    </div>
  );
}
